import { useId } from 'react';
import { t } from '@app/i18n';
import { useForm } from 'react-hook-form';
import { Drawer, ErrorBanner, LoadingButton } from '@zarya/ui';
import type { ApiError } from '@app/api/ApiError';
import {
  siteDefaults,
  templatePlaceholder,
  type SiteFormValues,
  type SiteItem,
  type SitePosture,
} from './SiteModel';
import { useSiteValidation } from './useFormValidation';
import { SiteForm } from './SiteForm';

export interface SiteDrawerProps {
  open: boolean;
  /** Ausente = **Novo site**. */
  site?: SiteItem;
  /** O vocabulário de postura do servidor, na ordem L1→L3. */
  postures: SitePosture[];
  /** `SiteList.template_default` — o template de site NOVO. */
  templateDefault: string;
  /** Erro da última tentativa de gravar (`slug_duplicado`, colisão de template, …). */
  error?: ApiError | null;
  pending?: boolean;
  onSubmit: (values: SiteFormValues) => void;
  onClose: () => void;
}

/**
 * Drawer de criar/editar site (P1, FR-131).
 *
 * O estado do formulário mora aqui porque o rodapé do drawer é quem submete: o
 * botão vive fora do `<form>` e o alcança por `form=`. Fechar com alteração
 * pendente pede confirmação — quem cuida disso é o próprio `Drawer`.
 */
export function SiteDrawer({
  open,
  site,
  postures,
  templateDefault,
  error,
  pending = false,
  onSubmit,
  onClose,
}: SiteDrawerProps) {
  const formId = useId();
  const editing = Boolean(site);

  const { control, handleSubmit, formState, reset } = useForm<SiteFormValues>({
    defaultValues: siteDefaults(site),
    ...useSiteValidation(),
  });

  function close() {
    reset(siteDefaults(site));
    onClose();
  }

  return (
    <Drawer
      open={open}
      title={t(editing ? 'sites.drawer.title.edit' : 'sites.drawer.title.new')}
      subtitle={t(editing ? 'sites.drawer.subtitle.edit' : 'sites.drawer.subtitle.new')}
      dirty={formState.isDirty && !pending}
      onClose={close}
      discardMessage={t('sites.drawer.discard')}
      footer={
        <LoadingButton
          type="submit"
          form={formId}
          loading={pending}
          disabled={!formState.isValid}
          loadingLabel={t(
            editing ? 'sites.drawer.submitting.edit' : 'sites.drawer.submitting.new',
          )}
        >
          {t(editing ? 'sites.drawer.submit.edit' : 'sites.drawer.submit.new')}
        </LoadingButton>
      }
    >
      {error ? (
        // `slug_duplicado` e a colisão de template mantêm o formulário como está
        // (FR-123): o operador corrige o campo em vez de digitar tudo de novo.
        <ErrorBanner
          error={error}
          escrita
          title={t(editing ? 'sites.drawer.error.edit' : 'sites.drawer.error.new')}
          hideLink
        />
      ) : null}

      <SiteForm
        control={control}
        editing={editing}
        site={site}
        postures={postures}
        templateHint={templatePlaceholder(site, templateDefault)}
        formId={formId}
        onSubmit={handleSubmit(onSubmit)}
      />
    </Drawer>
  );
}
